import { ToastContent, getToastContent } from "constants/toasts";
import { trackEngagement } from "helpers/analytics";
import { isInIframe } from "helpers/app";
import { openInNewCard } from "helpers/routing";
import { AgentsRoutes } from "routes/agents/constants";
import { AppStateProvider } from "services/app-state-provider";
import { selectIsAdmin } from "store/entities/agents";
import { LicenseActions } from "store/entities/license";
import { ActionsUnion, IAction, IActionWithPayload, createAction } from "store/helper";
import { history } from "store/history";
import { uniqueId } from "utils/string";

import {
  ICreateToastActionParams,
  IToastsCreatePayload,
  IToastsRemovePayload,
  IToastsShowPayload,
  ToastAutoHideDelay,
  ToastHorizontalPosition,
  ToastVariant,
  ToastVerticalPosition,
} from "./interfaces";

export enum ToastsActionsNames {
  CREATE = "FEATURE/TOASTS/CREATE",
  SHOW = "FEATURE/TOASTS/SHOW",
  REMOVE = "FEATURE/TOASTS/REMOVE",
  REMOVE_ALL = "FEATURE/TOASTS/REMOVE_ALL",
}

enum LimitExceeded {
  ERROR_TYPE = "limitExceeded",
  ERROR_MESSAGE_BEFORE_VERIFICATION = "number of emails sent exceeded before verification",
  ERROR_MESSAGE_HARD_BOUNCE_24_HOURS = "hard bounces rate per 24h exceeded",
  ERROR_MESSAGE_HARD_BOUNCE_7_DAYS = "hard bounces rate per 7d exceeded",
  COPY_BEFORE_VERIFICATION_ADMIN = "Action requires a verified HelpDesk account. Resend the verification link to your email address.",
  COPY_BEFORE_VERIFICATION_AGENT = "Action requires a verified HelpDesk account. Ask any admin to complete account verification.",
  COPY_HARD_BOUNCE_24_HOURS = "Temporary lock. Too many email hard-bounces generated in the last 24 hours.",
  COPY_HARD_BOUNCE_7_DAYS = "Temporary lock. Too many email hard-bounces generated in the last 7 days.",
}

enum ChildTicketMessages {
  CANNOT_MOVE = "Moving child ticket to another silo not allowed",
  CANNOT_SUMMARIZE = "Summarizing child ticket not allowed",
  CANNOT_UPDATE = "Child ticket update not allowed",
}

const mapErrorToToastMessage = (errorMessage: string, isOwner?: boolean): string => {
  if (errorMessage.includes(LimitExceeded.ERROR_MESSAGE_BEFORE_VERIFICATION)) {
    return isOwner ? LimitExceeded.COPY_BEFORE_VERIFICATION_ADMIN : LimitExceeded.COPY_BEFORE_VERIFICATION_AGENT;
  }
  if (errorMessage.includes(LimitExceeded.ERROR_MESSAGE_HARD_BOUNCE_24_HOURS)) {
    return LimitExceeded.COPY_HARD_BOUNCE_24_HOURS;
  }
  if (errorMessage.includes(LimitExceeded.ERROR_MESSAGE_HARD_BOUNCE_7_DAYS)) {
    return LimitExceeded.COPY_HARD_BOUNCE_7_DAYS;
  }

  if (errorMessage.includes(ChildTicketMessages.CANNOT_MOVE)) {
    return "Ticket has been merged and cannot be moved to another folder.";
  }

  if (errorMessage.includes(ChildTicketMessages.CANNOT_SUMMARIZE)) {
    return "This ticket has been merged and cannot be summarized.";
  }

  if (errorMessage.includes(ChildTicketMessages.CANNOT_UPDATE)) {
    return "This ticket has been merged and cannot be updated.";
  }

  return errorMessage;
};

export const ToastsActions = {
  createToast: (
    toastDetails: ICreateToastActionParams
  ): IActionWithPayload<ToastsActionsNames.CREATE, ICreateToastActionParams> => {
    const hasErrorMessage = !!toastDetails.error?.message;
    const shouldDisplayLimitExceededToast =
      hasErrorMessage && toastDetails.error
        ? toastDetails.error.type === LimitExceeded.ERROR_TYPE &&
          (toastDetails.error.message.includes(LimitExceeded.ERROR_MESSAGE_BEFORE_VERIFICATION) ||
            toastDetails.error.message.includes(LimitExceeded.ERROR_MESSAGE_HARD_BOUNCE_24_HOURS) ||
            toastDetails.error.message.includes(LimitExceeded.ERROR_MESSAGE_HARD_BOUNCE_7_DAYS))
        : false;

    const shouldShowChildTicketMessageToast =
      hasErrorMessage && toastDetails.error
        ? (toastDetails.error.type === "conflict" &&
            toastDetails.error.message.includes(ChildTicketMessages.CANNOT_MOVE)) ||
          toastDetails.error.message.includes(ChildTicketMessages.CANNOT_SUMMARIZE) ||
          toastDetails.error.message.includes(ChildTicketMessages.CANNOT_UPDATE)
        : false;

    const payload: IToastsCreatePayload = {
      autoHideDelayTime: ToastAutoHideDelay.Long,
      horizontalPosition: ToastHorizontalPosition.Center,
      variant: ToastVariant.Notification,
      verticalPosition: ToastVerticalPosition.Top,
      removable: true,
      content: getToastContent(ToastContent.DEFAULT_ERROR),
      id: uniqueId(),
      ...toastDetails,
      ...(shouldDisplayLimitExceededToast &&
        ((): ICreateToastActionParams => {
          if (!toastDetails.error?.message.includes(LimitExceeded.ERROR_MESSAGE_BEFORE_VERIFICATION)) {
            return {
              content: mapErrorToToastMessage(toastDetails.error?.message || ""),
              autoHideDelayTime: ToastAutoHideDelay.Infinite,
              removable: true,
            };
          }

          const isOwner = selectIsAdmin(AppStateProvider.getState());
          const isInFrame = isInIframe();

          const handleActionClick = (): void => {
            if (isOwner) {
              AppStateProvider.dispatch(LicenseActions.resendVerification());
            } else if (isInFrame) {
              openInNewCard(AgentsRoutes.Agents);
            } else {
              history.push(AgentsRoutes.Agents);
            }
            trackEngagement("Application toast: Unverified account message shown", undefined, {
              action: isOwner ? "resendLink" : "findAdmin",
              iframe: isInFrame,
            });
          };

          return {
            content: mapErrorToToastMessage(toastDetails.error.message, isOwner),
            autoHideDelayTime: ToastAutoHideDelay.Infinite,
            action: {
              handler: handleActionClick,
              label: isOwner ? "Resend link" : "Find admins",
              closeOnClick: true,
            },
          };
        })()),
      ...(shouldShowChildTicketMessageToast && {
        content: mapErrorToToastMessage(toastDetails.error?.message || ""),
      }),
    };

    return createAction(ToastsActionsNames.CREATE, payload);
  },

  showToast: (payload: IToastsShowPayload): IActionWithPayload<ToastsActionsNames.SHOW, IToastsShowPayload> =>
    createAction(ToastsActionsNames.SHOW, payload),
  removeToast: (payload: IToastsRemovePayload): IActionWithPayload<ToastsActionsNames.REMOVE, IToastsRemovePayload> =>
    createAction(ToastsActionsNames.REMOVE, payload),
  removeAllToasts: (): IAction<ToastsActionsNames.REMOVE_ALL> => createAction(ToastsActionsNames.REMOVE_ALL),
};

export type ToastsAction = ActionsUnion<typeof ToastsActions>;
